import React, { Component, lazy } from 'react';
import { withRouter } from 'react-router';
import { Switch } from 'react-router-dom';

import { RouteWrapper } from 'components/layout/helper';
import { ROUTES, PAGE_VIEW_EVENTS } from 'configs/routes/constants';
import { withShellContext } from 'hoc/withShellContext';
import withSuspense from 'hoc/withSuspense';
import { inject } from 'mobx-react';
import StreamingService from 'modules/configManager/StreamingService';
import { FeatureToggles } from 'modules/featureToggle';
import MembershipService from 'modules/membership/MembershipService';
import { filterOnlineEventRoutes } from 'modules/onlineEvent/helpers';
import PropTypes from 'prop-types';

import ContentMicrofront from '../contentMicrofront';

const AdvertisementEditor = lazy(() =>
  import('../../advertisementEditor/components/AdvertisementEditor')
);
const ContentForm = lazy(() => import('../ContentForm'));
const ContentListConcept = lazy(() => import('../contentListConcept'));
const ModuleEditor = lazy(() =>
  import('../../moduleEditor/components/ModuleEditor')
);
const QuizEditor = lazy(() => import('../../quiz/components/Quiz'));
const LiveForm = lazy(() => import('../webinar/components/LiveForm'));
const SubtitleEditor = lazy(() =>
  import('../../../subtitle/components/SubtitleEditor')
);
const Tracks = lazy(() => import('../../tracks/list/Tracks'));
const NewTrack = lazy(() => import('../../tracks/create/NewTrack'));
const Track = lazy(() => import('../../tracks/view/TrackView'));
const EditTrack = lazy(() => import('../../tracks/edit/EditTrack'));
const ContentImportPage = lazy(() =>
  import('../../../contentImport/components/contentImportPage')
);

@withRouter
@withShellContext
@inject((stores) => ({
  onlineEvent: stores.MembershipStore.onlineEvent,
  StreamingStore: stores.StreamingStore,
  createModuleConceptToggle: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.CreateModuleConcept
  ),
  createContentConceptToggle: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.CreateContentConcept
  )
}))
class ContentIndex extends Component {
  static propTypes = {
    onlineEvent: PropTypes.bool,
    isMembership: PropTypes.bool,
    StreamingStore: PropTypes.shape({
      setStatus: PropTypes.func
    })
  };

  componentDidMount() {
    const { StreamingStore } = this.props;

    StreamingService.fetchStatus().then((res) => {
      MembershipService.setStreamingStatus(res.streamingActive);
      StreamingStore.setStatus(res);
    });
  }

  get isCreateModuleConceptEnabled() {
    const { isMembership, createModuleConceptToggle } = this.props;

    return isMembership && createModuleConceptToggle;
  }

  get isCreateContentConceptEnabled() {
    const { isMembership, createContentConceptToggle } = this.props;

    return isMembership && createContentConceptToggle;
  }

  get moduleListRoute() {
    const routes = [ROUTES.CONTENT.LIST];

    if (this.isCreateModuleConceptEnabled) {
      routes.push(ROUTES.CONTENT.NEW_MODULE, ROUTES.CONTENT.EDIT_MODULE);
    }

    if (this.isCreateContentConceptEnabled) {
      routes.push(ROUTES.CONTENT.ADD_CONTENT, ROUTES.CONTENT.EDIT_CONTENT);
    }

    return {
      path: `(${routes.join('|')})`,
      track: PAGE_VIEW_EVENTS.CONTENT.MAIN,
      render: ContentListConcept
    };
  }

  get moduleEditorRoutes() {
    const addPath = this.isCreateModuleConceptEnabled
      ? ROUTES.CONTENT.NEW_EXTRA_PAID_MODULE
      : ROUTES.CONTENT.NEW_MODULE;

    const editPath = this.isCreateModuleConceptEnabled
      ? ROUTES.CONTENT.EDIT_EXTRA_PAID_MODULE
      : ROUTES.CONTENT.EDIT_MODULE;

    return [
      {
        path: addPath,
        track: PAGE_VIEW_EVENTS.CONTENT.NEW_MODULE,
        render: ModuleEditor
      },
      {
        path: editPath,
        track: PAGE_VIEW_EVENTS.CONTENT.EDIT_MODULE,
        render: ModuleEditor
      }
    ];
  }

  get moduleContentPageEditorRoutes() {
    if (this.isCreateContentConceptEnabled) {
      return [];
    }

    return [
      {
        path: ROUTES.CONTENT.ADD_CONTENT,
        track: PAGE_VIEW_EVENTS.CONTENT.ADD_CONTENT,
        render: ContentForm
      },
      {
        path: ROUTES.CONTENT.EDIT_CONTENT,
        track: PAGE_VIEW_EVENTS.CONTENT.EDIT_CONTENT,
        render: ContentForm
      }
    ];
  }

  get moduleRoutes() {
    const { onlineEvent } = this.props;

    const allRoutes = [
      {
        path: ROUTES.CONTENT.REVIEW_SUBTITLE,
        track: PAGE_VIEW_EVENTS.CONTENT.REVIEW_SUBTITLE,
        render: SubtitleEditor
      },
      {
        path: ROUTES.CONTENT.IMPORT,
        track: PAGE_VIEW_EVENTS.CONTENT.IMPORT,
        render: ContentImportPage
      },

      {
        path: ROUTES.CONTENT.NEW_QUIZ,
        track: PAGE_VIEW_EVENTS.CONTENT.NEW_QUIZ,
        render: QuizEditor
      },
      {
        path: ROUTES.CONTENT.EDIT_QUIZ,
        track: PAGE_VIEW_EVENTS.CONTENT.EDIT_QUIZ,
        render: QuizEditor
      },
      {
        path: ROUTES.CONTENT.NEW_ADVERTISEMENT,
        track: PAGE_VIEW_EVENTS.CONTENT.NEW_ADVERTISEMENT,
        render: AdvertisementEditor
      },
      {
        path: ROUTES.CONTENT.EDIT_ADVERTISEMENT,
        track: PAGE_VIEW_EVENTS.CONTENT.EDIT_ADVERTISEMENT,
        render: AdvertisementEditor
      },
      {
        path: ROUTES.CONTENT.NEW_WEBINAR,
        track: PAGE_VIEW_EVENTS.CONTENT.NEW_WEBINAR,
        render: LiveForm
      },
      {
        path: ROUTES.CONTENT.EDIT_WEBINAR,
        track: PAGE_VIEW_EVENTS.CONTENT.EDIT_WEBINAR,
        render: LiveForm
      },
      ...this.moduleContentPageEditorRoutes,
      ...this.moduleEditorRoutes,
      this.moduleListRoute
    ];

    return onlineEvent ? filterOnlineEventRoutes(allRoutes) : allRoutes;
  }

  get trackRoutes() {
    return [
      {
        path: ROUTES.CONTENT.NEW_TRACK,
        track: PAGE_VIEW_EVENTS.TRACKS.NEW,
        render: NewTrack
      },
      {
        path: ROUTES.CONTENT.EDIT_TRACK,
        track: PAGE_VIEW_EVENTS.TRACKS.EDIT,
        render: EditTrack
      },
      {
        path: ROUTES.CONTENT.VIEW_TRACK,
        track: PAGE_VIEW_EVENTS.TRACKS.VIEW,
        render: Track
      },
      {
        path: ROUTES.CONTENT.TRACKS,
        track: PAGE_VIEW_EVENTS.TRACKS.LIST,
        render: Tracks
      }
    ];
  }

  render() {
    const { onlineEvent, createModuleConceptToggle, isMembership } = this.props;
    const shouldRenderContentMicrofront =
      createModuleConceptToggle && isMembership;
    let routes = this.trackRoutes.concat(this.moduleRoutes);

    if (onlineEvent) {
      routes = filterOnlineEventRoutes(routes);
    }

    return (
      <>
        <Switch>
          {routes.map((route) => (
            <RouteWrapper
              track={route.track}
              key={route.path}
              path={route.path}
              render={withSuspense(route.render)}
            />
          ))}
        </Switch>
        {shouldRenderContentMicrofront && <ContentMicrofront />}
      </>
    );
  }
}

export default ContentIndex;
